import { useEffect, useRef } from "react";
import { useLocation } from "react-router";

/**
 * Moves the focus to the element named by the fragment of the URL (`/corrections#approvals`) once
 * it is rendered, which also scrolls it into view below the sticky header (UX-007). The router
 * does not scroll to fragments, and the target may appear only after data has loaded. Each
 * fragment is handled once, so later renders do not take the focus away again. The target needs
 * `tabIndex={-1}` if it is not focusable by itself, for example a section heading.
 *
 * @param ready - Whether the targets are rendered.
 */
export function useFocusHashTarget(ready: boolean): void {
  const { hash } = useLocation();
  const handledRef = useRef<string | null>(null);

  useEffect(() => {
    if (!ready || hash === "" || handledRef.current === hash) {
      return;
    }

    const target = document.getElementById(decodeURIComponent(hash.slice(1)));

    if (target !== null) {
      handledRef.current = hash;
      target.focus();
    }
  }, [hash, ready]);
}
